import {useEffect} from 'react';

import {type RunSignature} from '../../../types/run';
import {
  getPanelFilePathKey,
  IDLE_PANEL_FILE_BLOB,
  type PanelFileBlobSnapshot,
  useWorkspaceBlobStore,
} from './panelBlobStore';

type UseBlobArgs = {
  runSignature: RunSignature;
  filePath: string | null | undefined;
  directURL: string | null | undefined;
  logFailure?: boolean;
  /** Resolves with a fresh `directURL`; see `FetchIfNeededOptions`. */
  onUrlExpired?: () => Promise<string | null | undefined>;
};

/**
 * Workspace-scoped blob bytes keyed by run + file path (MEDIA-3E).
 * Fetches via the shared workspace blob store so entries survive panel
 * unmounts and are reused across panels with the same file path key.
 */
export function useBlob({
  runSignature,
  filePath,
  directURL,
  logFailure = true,
  onUrlExpired,
}: UseBlobArgs): PanelFileBlobSnapshot {
  const filePathKey =
    filePath != null ? getPanelFilePathKey(runSignature, filePath) : null;

  const fetchIfNeeded = useWorkspaceBlobStore(state => state.fetchIfNeeded);
  const touch = useWorkspaceBlobStore(state => state.touch);
  const snapshot = useWorkspaceBlobStore(state =>
    filePathKey != null
      ? (state.entries[filePathKey] ?? IDLE_PANEL_FILE_BLOB)
      : IDLE_PANEL_FILE_BLOB
  );

  useEffect(() => {
    if (filePathKey == null) {
      return;
    }
    fetchIfNeeded(filePathKey, directURL ?? null, {logFailure, onUrlExpired});
  }, [directURL, fetchIfNeeded, filePathKey, logFailure, onUrlExpired]);

  useEffect(() => {
    if (filePathKey == null) {
      return;
    }
    touch(filePathKey);
  }, [filePathKey, touch]);

  return snapshot;
}
